import { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { Captions, ChevronLeft, FolderInput, Mic, MoreHorizontal, PencilLine, Plus, Trash2, Upload } from "lucide-react";

import { extractErrorMessage } from "utils/apiError";
import { toast } from "utils/toast";
import { filenameForUpload, findPersistedUpload, isUploadCancelledError, transcriptionFromUpload } from "utils/upload";
import { useDirectUpload } from "hooks/use-direct-upload";
import { useIsMobile } from "hooks/use-mobile";
import { detectTextLang, translations } from "utils/i18n";
import { transcriptName } from "utils/transcript";
import { useLanguage } from "context/languageContext";
import { Button } from "components/ui/button";
import { SidebarTrigger } from "components/ui/sidebar";
import { TranscriptionList } from "components/transcription-list";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "components/ui/dropdown-menu";
import { ImportTranscriptionDialog } from "components/import-transcription-dialog";
import { NameDialog } from "components/name-dialog";
import { UploadDialog } from "components/upload-dialog";
import { RecordDialog } from "components/record-dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "components/ui/alert-dialog";
import { useProjectsContext } from "pages/ProjectsLayout";

const CAPTION_FORMATS = ["MP4", "MOV", "MKV", "WEBM"];

export default function ProjectDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const {
    projects,
    items,
    upsertItem,
    renameProject,
    deleteProject,
    deleteItem,
    renameItem,
    moveItemToProject,
  } = useProjectsContext();

  const { language } = useLanguage();
  const tr = translations[language].projectDetail;
  const trTiles = translations[language].actionTiles;
  const trList = translations[language].transcriptionList;
  const trUpload = translations[language].uploadFlow;

  // Same three entry points as the sidebar's "Create" menu (see
  // app-sidebar.js) — copy lives once in the `actionTiles` translation
  // section, shared with the home tiles (see action-tiles.js).
  const NEW_TRANSCRIPTION_OPTIONS = [
    { dialog: "upload", icon: Upload, label: trTiles.createTitle },
    { dialog: "record", icon: Mic, label: trTiles.recordTitle },
    { dialog: "caption", icon: Captions, label: trTiles.captionTitle },
  ];

  const upload = useDirectUpload({ scope: `project:${id}` });
  const [renameProjectOpen, setRenameProjectOpen] = useState(false);
  const [deleteProjectOpen, setDeleteProjectOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [renameTarget, setRenameTarget] = useState(null);
  const [activeDialog, setActiveDialog] = useState(null); // null | "upload" | "record" | "caption"
  const [importOpen, setImportOpen] = useState(false);

  const project = projects.find((p) => p.id === id);

  const applyCompletedUpload = useCallback(
    (completedUpload, meta) => {
      const transcript = transcriptionFromUpload(completedUpload, meta);
      if (!transcript) throw new Error(trUpload.missingIdError);
      upsertItem(transcript);
    },
    [upsertItem, trUpload]
  );

  // Reattach to whatever upload was in flight before this page last
  // unmounted (a refresh, or navigating away and back) — see
  // useDirectUpload's `scope` option. The ref guards against React
  // StrictMode's dev-only double-invoke calling resume() twice.
  const resumedRef = useRef(false);
  useEffect(() => {
    if (resumedRef.current) return;
    resumedRef.current = true;
    const record = findPersistedUpload(`project:${id}`);
    if (!record) return;
    upload
      .resume(record)
      .then((completedUpload) => {
        applyCompletedUpload(completedUpload, record);
        toast.success(trUpload.uploadComplete);
      })
      .catch((err) => {
        if (!isUploadCancelledError(err)) toast.error(extractErrorMessage(err));
      })
      .finally(() => setActiveDialog(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function handleUpload(fileOrBlob, source, customName) {
    try {
      const filename = filenameForUpload(fileOrBlob, source === "recorded" ? customName : undefined);
      const name = customName || filename;
      const transcriptLanguage = "ar";
      const meta = { filename, language: transcriptLanguage, source, name, project: id };
      const completedUpload = await upload.start(fileOrBlob, meta);
      applyCompletedUpload(completedUpload, meta);
      toast.success(trUpload.uploadComplete);
    } catch (err) {
      if (isUploadCancelledError(err)) toast.info(trUpload.uploadCancelled);
      else toast.error(extractErrorMessage(err));
      throw err;
    }
  }

  useEffect(() => {
    if (!upload.busy || activeDialog) return;
    if (upload.source === "recorded") setActiveDialog("record");
    else if (upload.source === "caption") setActiveDialog("caption");
    else setActiveDialog("upload");
  }, [activeDialog, upload.busy, upload.source]);

  if (!project) return <Navigate to="/projects" replace />;

  const projectItems = items.filter((t) => t.project === id);
  const unassignedItems = items.filter((t) => !t.project);
  const uploading = upload.busy;
  const visibleFileCount = projectItems.length;

  return (
    <div className="mx-auto w-full max-w-5xl px-6 py-8 md:px-10 md:py-10">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="grid min-w-0 gap-1">
          <div className="flex min-w-0 items-center gap-1">
            {/* ProjectsPanel (which normally carries this) hides on mobile
                while a project is open — see ProjectsLayout — so this is
                the only way back to the main sidebar in that state. */}
            {isMobile && <SidebarTrigger className="shrink-0 text-muted-foreground" />}
            <Button variant="ghost" size="icon-sm" onClick={() => navigate("/projects")} className="shrink-0 text-muted-foreground">
              <ChevronLeft className="rtl:rotate-180" />
              <span className="sr-only">{tr.backToProjects}</span>
            </Button>
            <h1 lang={detectTextLang(project.name)} className="truncate text-2xl font-semibold tracking-tight text-foreground">
              {project.name}
            </h1>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon-sm" className="shrink-0 text-muted-foreground">
                  <MoreHorizontal />
                  <span className="sr-only">{tr.projectActions}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuItem onClick={() => setRenameProjectOpen(true)}>
                  <PencilLine className="size-4" />
                  {tr.menuRename}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => setDeleteProjectOpen(true)}>
                  <Trash2 className="size-4" />
                  {tr.menuDelete}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <p className="ps-9 text-sm text-muted-foreground">{trList.filesCount(visibleFileCount)}</p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={() => setImportOpen(true)}>
            <FolderInput className="size-4 rtl:-scale-x-100" />
            {tr.importLabel}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="blue" disabled={uploading}>
                <Plus className="size-4" />
                {uploading ? tr.uploadingLabel : tr.newTranscription}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {NEW_TRANSCRIPTION_OPTIONS.map(({ dialog, icon: Icon, label }) => (
                <DropdownMenuItem key={dialog} onClick={() => setActiveDialog(dialog)}>
                  <Icon className="size-4" />
                  {label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="pt-8">
        <TranscriptionList
          items={projectItems}
          projects={projects}
          onRename={setRenameTarget}
          onDelete={setDeleteTarget}
          onMoveToProject={moveItemToProject}
        />
      </div>

      <NameDialog
        open={renameProjectOpen}
        onOpenChange={setRenameProjectOpen}
        title={tr.renameProjectTitle}
        label={tr.nameLabel}
        initialValue={project.name}
        submitLabel={tr.saveLabel}
        onSubmit={(name) => renameProject(id, name)}
      />

      <AlertDialog open={deleteProjectOpen} onOpenChange={setDeleteProjectOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{tr.deleteProjectTitle}</AlertDialogTitle>
            <AlertDialogDescription>{tr.deleteProjectDescription}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tr.cancelLabel}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                setDeleteProjectOpen(false);
                deleteProject(id)
                  .then(() => navigate("/projects"))
                  .catch(() => {});
              }}
            >
              {tr.menuDelete}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{trList.deleteTranscriptionTitle}</AlertDialogTitle>
            <AlertDialogDescription>{trList.deleteTranscriptionDescription}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{trList.cancelLabel}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (deleteTarget) deleteItem(deleteTarget.id);
                setDeleteTarget(null);
              }}
            >
              {trList.delete}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <NameDialog
        open={!!renameTarget}
        onOpenChange={(open) => !open && setRenameTarget(null)}
        title={trList.renameTranscriptionTitle}
        label={trList.nameLabel}
        initialValue={renameTarget ? transcriptName(renameTarget, language) : ""}
        submitLabel={trList.saveLabel}
        onSubmit={(value) => {
          if (renameTarget) renameItem(renameTarget.id, value);
        }}
      />

      <UploadDialog
        open={activeDialog === "upload"}
        onOpenChange={(open) => setActiveDialog(open ? "upload" : null)}
        onUpload={handleUpload}
        uploading={uploading}
        upload={upload}
      />
      <RecordDialog
        open={activeDialog === "record"}
        onOpenChange={(open) => setActiveDialog(open ? "record" : null)}
        onUpload={handleUpload}
        uploading={uploading}
        upload={upload}
      />
      <UploadDialog
        open={activeDialog === "caption"}
        onOpenChange={(open) => setActiveDialog(open ? "caption" : null)}
        onUpload={handleUpload}
        uploading={uploading}
        upload={upload}
        source="caption"
        title={trUpload.captionUploadTitle}
        prompt={trUpload.captionUploadPrompt}
        accept="video/*"
        formats={CAPTION_FORMATS}
      />

      <ImportTranscriptionDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        items={unassignedItems}
        onImport={(item, options) => moveItemToProject(item, id, options)}
      />
    </div>
  );
}
